import React, { useState, useMemo } from 'react';
import {
  IoList,
  IoCalendar,
  IoAdd,
  IoClose,
  IoPencil,
  IoCheckmarkCircle,
  IoTime,
  IoDocumentText,
  IoCalendarOutline,
  IoCash,
  IoStar,
  IoChevronBack,
  IoChevronForward,
  IoCheckmark,
  IoCloseCircle,
  IoOpenOutline,
} from 'react-icons/io5';
import InfoButton from '@/components/InfoButton';
import ToastService from '@/utils/toast';
import { useDeadlines, useCreateDeadline, useCompleteDeadline, useUpdateDeadline, useDeleteDeadline } from '@/hooks/useDeadlines';
import { DeadlineCreate, DeadlinePriority, DeadlineSource, formatPriority, PriorityColors } from '@/types/deadline';
import './Deadlines.css';

const Deadlines: React.FC = () => {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditFormModal, setShowEditFormModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedDeadlineId, setSelectedDeadlineId] = useState<number | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filters, setFilters] = useState<{
    priority?: DeadlinePriority;
    completed?: boolean;
  }>({});
  const [sourceFilters, setSourceFilters] = useState({
    application_materials: true,
    university_deadline: true,
    demonstrated_interest: true,
    counselor_shared: true,
    self_created: true,
  });

  // Fetch deadlines from API with filters
  const { data: deadlinesData, isLoading, error } = useDeadlines(filters);
  const { mutate: createDeadline, isPending: isCreating } = useCreateDeadline();
  const { mutate: completeDeadline } = useCompleteDeadline();
  const { mutate: updateDeadline, isPending: isUpdating } = useUpdateDeadline();
  const { mutate: deleteDeadline, isPending: isDeleting } = useDeleteDeadline();

  const allDeadlines = deadlinesData?.deadlines || [];
  
  // Filter deadlines by source type based on checkbox filters
  const deadlines = useMemo(() => {
    return allDeadlines.filter((deadline) => {
      return sourceFilters[deadline.source_type as keyof typeof sourceFilters] !== false;
    });
  }, [allDeadlines, sourceFilters]);

  // Check if error is a network/connection error (backend not running)
  const isConnectionError = error && (
    error.message?.includes('Network Error') ||
    error.message?.includes('ERR_CONNECTION_REFUSED') ||
    (error as any).code === 'ERR_NETWORK'
  );

  // Calculate days left for a deadline
  const getDaysLeft = (dueDate: string): number => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get color classes based on source type for list view
  const getSourceTypeColor = (sourceType: DeadlineSource) => {
    switch (sourceType) {
      case DeadlineSource.APPLICATION_MATERIALS:
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-l-4 border-blue-500',
          icon: 'bg-blue-100 dark:bg-blue-900/30',
          iconText: 'text-blue-600 dark:text-blue-400',
        };
      case DeadlineSource.UNIVERSITY_DEADLINE:
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-l-4 border-red-500',
          icon: 'bg-red-100 dark:bg-red-900/30',
          iconText: 'text-red-600 dark:text-red-400',
        };
      case DeadlineSource.DEMONSTRATED_INTEREST:
        return {
          bg: 'bg-purple-50 dark:bg-purple-900/20',
          border: 'border-l-4 border-purple-500',
          icon: 'bg-purple-100 dark:bg-purple-900/30',
          iconText: 'text-purple-600 dark:text-purple-400',
        };
      case DeadlineSource.COUNSELOR_SHARED:
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-l-4 border-orange-500',
          icon: 'bg-orange-100 dark:bg-orange-900/30',
          iconText: 'text-orange-600 dark:text-orange-400',
        };
      case DeadlineSource.SELF_CREATED:
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-l-4 border-green-500',
          icon: 'bg-green-100 dark:bg-green-900/30',
          iconText: 'text-green-600 dark:text-green-400',
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-800',
          border: 'border-l-4 border-gray-500',
          icon: 'bg-gray-100 dark:bg-gray-700',
          iconText: 'text-gray-600 dark:text-gray-400',
        };
    }
  };

  // Get color classes for calendar events
  const getCalendarEventColor = (sourceType: DeadlineSource) => {
    switch (sourceType) {
      case DeadlineSource.APPLICATION_MATERIALS:
        return 'bg-blue-100 dark:bg-blue-900/50';
      case DeadlineSource.UNIVERSITY_DEADLINE:
        return 'bg-green-100 dark:bg-green-900/50';
      case DeadlineSource.DEMONSTRATED_INTEREST:
        return 'bg-purple-100 dark:bg-purple-900/50';
      case DeadlineSource.COUNSELOR_SHARED:
        return 'bg-orange-100 dark:bg-orange-900/50';
      case DeadlineSource.SELF_CREATED:
        return 'bg-red-100 dark:bg-red-900/50';
      default:
        return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  // Group deadlines by date for list view
  const deadlinesByDate = useMemo(() => {
    return deadlines.reduce((acc, deadline) => {
      const date = new Date(deadline.due_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(deadline);
      return acc;
    }, {} as Record<string, typeof deadlines>);
  }, [deadlines]);

  const getDateSquare = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    return { day, month };
  };

  const getStatusBadgeColor = (daysLeft: number) => {
    if (daysLeft <= 7) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    if (daysLeft <= 14) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  };

  const handleEditClick = (deadlineId: number) => {
    setSelectedDeadlineId(deadlineId);
    setShowEditModal(true);
  };

  const handleAddDeadline = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const title = formData.get('taskName') as string;
    const deadlineDatetime = formData.get('deadlineDatetime') as string;
    const priority = formData.get('priority') as DeadlinePriority;
    const info = formData.get('info') as string;
    const estHours = formData.get('estHours') as string;
    const estMinutes = formData.get('estMinutes') as string;

    // Calculate total expected hours and round to 1 decimal place
    const hours = estHours ? parseFloat(estHours) : 0;
    const minutes = estMinutes ? parseFloat(estMinutes) : 0;
    const expectedHours = Math.round((hours + minutes / 60) * 10) / 10;

    // Convert local datetime to ISO string (datetime-local input is already in local timezone)
    const deadlineData: DeadlineCreate = {
      title,
      due_date: new Date(deadlineDatetime).toISOString(),
      priority: priority || DeadlinePriority.MEDIUM,
      info: info || undefined,
      expected_hours: expectedHours > 0 ? expectedHours : undefined,
    };

    createDeadline(deadlineData, {
      onSuccess: () => {
        ToastService.showSuccess('Deadline created successfully!');
        setShowAddModal(false);
        e.currentTarget.reset();
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.detail || 'Failed to create deadline';
        ToastService.showError(errorMessage);
      },
    });
  };

  const selectedDeadline = selectedDeadlineId
    ? deadlines.find((d) => d.id === selectedDeadlineId)
    : null;

  const renderEditOptions = () => {
    if (!selectedDeadline) return null;

    return (
      <div className="space-y-3">
        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Priority</span>
            <span
              className={`text-sm font-bold px-3 py-1 rounded-full ${PriorityColors[selectedDeadline.priority].bg} ${PriorityColors[selectedDeadline.priority].text}`}
            >
              {formatPriority(selectedDeadline.priority)}
            </span>
          </div>
          {selectedDeadline.expected_hours && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mt-2">
              <IoTime className="text-gray-400 dark:text-gray-500 mr-2" />
              <span>
                Expected time: <span className="font-semibold">{selectedDeadline.expected_hours.toFixed(1)} hours</span>
              </span>
            </div>
          )}
          {selectedDeadline.info && (
            <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
              <p className="font-semibold mb-1">Details:</p>
              <p className="whitespace-pre-wrap">{selectedDeadline.info}</p>
            </div>
          )}
        </div>

        <button
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
          onClick={() => {
            setShowEditModal(false);
            setShowEditFormModal(true);
          }}
        >
          <IoPencil />
          <span className="font-medium">Edit Deadline</span>
        </button>

        <button
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition-all"
          onClick={() => {
            completeDeadline(
              { id: selectedDeadline.id, completed: !selectedDeadline.completed },
              {
                onSuccess: () => {
                  ToastService.showSuccess(
                    selectedDeadline.completed
                      ? 'Deadline marked as incomplete!'
                      : 'Deadline marked as complete!'
                  );
                  setShowEditModal(false);
                },
                onError: () => {
                  ToastService.showError('Failed to update deadline status');
                },
              }
            );
          }}
        >
          <IoCheckmark />
          <span className="font-medium">
            {selectedDeadline.completed ? 'Mark as Incomplete' : 'Mark as Complete'}
          </span>
        </button>

        <button
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-all"
          onClick={() => {
            setShowEditModal(false);
            setShowDeleteConfirmModal(true);
          }}
        >
          <IoCloseCircle />
          <span className="font-medium">Delete Deadline</span>
        </button>
      </div>
    );
  };

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    const today = new Date();
    const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthLastDay - i),
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        isToday: isCurrentMonth && day === today.getDate(),
        date: new Date(year, month, day),
      });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i),
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  // Get deadlines for a specific date
  const getDeadlinesForDate = (date: Date) => {
    return deadlines.filter((deadline) => {
      const deadlineDate = new Date(deadline.due_date);
      return (
        deadlineDate.getDate() === date.getDate() &&
        deadlineDate.getMonth() === date.getMonth() &&
        deadlineDate.getFullYear() === date.getFullYear()
      );
    });
  };

  // Show error only if it's not a connection error
  // If backend is not running, treat it like empty state
  if (error && !isConnectionError) {
    return (
      <div className="deadlines-page dark:bg-gray-900">
        <div className="deadlines-container">
          <div className="p-8 text-center">
            <p className="text-red-600 dark:text-red-400">Failed to load deadlines. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="deadlines-page dark:bg-gray-900">
      <div className="deadlines-container">
        {/* Header */}
        <div className="deadlines-header">
          <h1 className="deadlines-title dark:text-gray-100">
            Deadlines
            <InfoButton elementId="deadlines-calendar-view" />
          </h1>
          <button
            className="add-deadline-header-btn dark:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setShowAddModal(true)}
            disabled={!!isConnectionError}
            title={isConnectionError ? 'Backend not running' : 'Add a new deadline'}
          >
            <IoAdd className="mr-2" />
            Add Deadline
            <InfoButton elementId="deadlines-add-custom" />
          </button>
        </div>

        {/* View Toggle Section */}
        <div className="view-toggle-section dark:bg-gray-800 dark:border-gray-700">
          <div className="view-toggle-buttons">
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active dark:bg-blue-600' : 'dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'}`}
              onClick={() => setViewMode('list')}
            >
              <IoList className="mr-2" />
              List View
              {viewMode === 'list' && <InfoButton elementId="deadlines-list-view" />}
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'calendar' ? 'active dark:bg-blue-600' : 'dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'}`}
              onClick={() => setViewMode('calendar')}
            >
              <IoCalendar className="mr-2" />
              Calendar View
              {viewMode === 'calendar' && <InfoButton elementId="deadlines-calendar-view" />}
            </button>
          </div>

          {/* Status/Priority Filter Buttons - Only show in list view */}
          {viewMode === 'list' && (
            <div className="filter-buttons">
              <button
                className={`filter-btn ${
                  !filters.completed && !filters.priority
                    ? 'active dark:bg-blue-600 dark:text-white'
                    : 'dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
                onClick={() => setFilters({})}
              >
                All Deadlines
              </button>
              <button
                className={`filter-btn ${
                  filters.completed === false
                    ? 'active dark:bg-blue-600 dark:text-white'
                    : 'dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
                onClick={() => setFilters({ completed: false })}
              >
                Incomplete
              </button>
              <button
                className={`filter-btn ${
                  filters.completed === true
                    ? 'active dark:bg-blue-600 dark:text-white'
                    : 'dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
                onClick={() => setFilters({ completed: true })}
              >
                Completed
              </button>
              <button
                className={`filter-btn ${
                  filters.priority === DeadlinePriority.HIGH
                    ? 'active dark:bg-blue-600 dark:text-white'
                    : 'dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
                onClick={() => setFilters({ priority: DeadlinePriority.HIGH })}
              >
                High Priority
              </button>
            </div>
          )}
        </div>

        {/* Source Type Filters */}
        <div className="list-view-filters dark:bg-gray-800 dark:border-gray-700">
          <div className="filter-checkboxes">
            <label className="filter-checkbox dark:text-gray-300">
              <input
                type="checkbox"
                checked={sourceFilters.application_materials}
                onChange={(e) =>
                  setSourceFilters({ ...sourceFilters, application_materials: e.target.checked })
                }
                className="checkbox-input"
              />
              <span className="filter-label">
                <span className="filter-dot bg-blue-500" />
                Application Materials
              </span>
            </label>
            <label className="filter-checkbox dark:text-gray-300">
              <input
                type="checkbox"
                checked={sourceFilters.university_deadline}
                onChange={(e) =>
                  setSourceFilters({ ...sourceFilters, university_deadline: e.target.checked })
                }
                className="checkbox-input"
              />
              <span className="filter-label">
                <span className="filter-dot bg-green-500" />
                University Deadlines
              </span>
            </label>
            <label className="filter-checkbox dark:text-gray-300">
              <input
                type="checkbox"
                checked={sourceFilters.demonstrated_interest}
                onChange={(e) =>
                  setSourceFilters({ ...sourceFilters, demonstrated_interest: e.target.checked })
                }
                className="checkbox-input"
              />
              <span className="filter-label">
                <span className="filter-dot bg-purple-500" />
                Demonstrated Interest
              </span>
            </label>
            <label className="filter-checkbox dark:text-gray-300">
              <input
                type="checkbox"
                checked={sourceFilters.counselor_shared}
                onChange={(e) =>
                  setSourceFilters({ ...sourceFilters, counselor_shared: e.target.checked })
                }
                className="checkbox-input"
              />
              <span className="filter-label">
                <span className="filter-dot bg-orange-500" />
                From my Counselor
              </span>
            </label>
            <label className="filter-checkbox dark:text-gray-300">
              <input
                type="checkbox"
                checked={sourceFilters.self_created}
                onChange={(e) => setSourceFilters({ ...sourceFilters, self_created: e.target.checked })}
                className="checkbox-input"
              />
              <span className="filter-label">
                <span className="filter-dot bg-red-500" />
                My Deadlines
              </span>
            </label>
          </div>
        </div>

        {isLoading && !isConnectionError ? (
          <div className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">Loading deadlines...</p>
          </div>
        ) : deadlines.length === 0 || isConnectionError ? (
          <div className="p-8 text-center">
            <IoCalendarOutline className="mx-auto text-6xl text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No deadlines yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {isConnectionError
                ? 'Backend is not running. Start the backend to create and manage deadlines.'
                : 'Create your first deadline to start tracking your tasks'}
            </p>
            {!isConnectionError && (
              <button
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                onClick={() => setShowAddModal(true)}
              >
                <IoAdd className="inline mr-2" />
                Add Your First Deadline
              </button>
            )}
            {isConnectionError && (
              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg max-w-md mx-auto">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  💡 To start the backend, run: <code className="bg-yellow-100 dark:bg-yellow-900/40 px-2 py-1 rounded">cd apply.ai_backend-main && ./start_backend.sh</code>
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* List View */}
            {viewMode === 'list' && (
              <div className="list-view-content">
                <div className="deadlines-list">
                  {Object.entries(deadlinesByDate).map(([date, dateDeadlines]) => {
                    const { day, month } = getDateSquare(date);
                    return (
                      <div key={date} className="deadline-date-group">
                        <div className="date-header">
                          <div className="date-square dark:bg-gray-700 dark:border-gray-600">
                            <span className="date-day dark:text-gray-100">{day}</span>
                            <span className="date-month dark:text-gray-400">{month}</span>
                          </div>
                          <div className="date-divider dark:bg-gray-700" />
                          <span className="date-count dark:text-gray-300">
                            {dateDeadlines.length} deadline{dateDeadlines.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                        <div className="deadline-items">
                          {dateDeadlines.map((deadline) => {
                            const daysLeft = getDaysLeft(deadline.due_date);
                            const dueDate = new Date(deadline.due_date);
                            const sourceColors = getSourceTypeColor(deadline.source_type);
                            return (
                              <div
                                key={deadline.id}
                                className={`deadline-item ${sourceColors.bg} ${sourceColors.border} dark:border-gray-700`}
                              >
                                <div className="deadline-content">
                                  <div className={`deadline-icon ${sourceColors.icon}`}>
                                    <IoDocumentText className={`${sourceColors.iconText} text-xl`} />
                                  </div>
                                  <div className="deadline-details">
                                    <div className="deadline-header-info">
                                      <h3 className="deadline-name dark:text-gray-100">{deadline.title}</h3>
                                      <span
                                        className={`type-badge px-3 py-1 rounded-full text-sm font-medium ${PriorityColors[deadline.priority].bg} ${PriorityColors[deadline.priority].text}`}
                                      >
                                        {formatPriority(deadline.priority)} Priority
                                      </span>
                                      <span className={`days-left-badge ${getStatusBadgeColor(daysLeft)}`}>
                                        {daysLeft > 0 ? `${daysLeft} days left` : daysLeft === 0 ? 'Due today' : 'Overdue'}
                                      </span>
                                    </div>
                                    {deadline.info && (
                                      <p className="deadline-description dark:text-gray-300">{deadline.info}</p>
                                    )}
                                    <div className="deadline-meta dark:text-gray-400">
                                      <div className="meta-item">
                                        <IoCalendarOutline className="meta-icon" />
                                        <span>
                                          Due: {dueDate.toLocaleDateString('en-US', {
                                            month: 'long',
                                            day: 'numeric',
                                            year: 'numeric',
                                          })}
                                        </span>
                                      </div>
                                      <div className="meta-item">
                                        <IoTime className="meta-icon" />
                                        <span>
                                          {dueDate.toLocaleTimeString('en-US', {
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true,
                                          })}
                                        </span>
                                      </div>
                                      {deadline.expected_hours && (
                                        <div className="meta-item">
                                          <IoTime className="meta-icon" />
                                          <span>Est. time: {deadline.expected_hours.toFixed(1)} hours</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="deadline-actions">
                                  <button
                                    className="edit-btn dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                                    onClick={() => handleEditClick(deadline.id)}
                                  >
                                    <IoPencil />
                                  </button>
                                  {deadline.source_type === DeadlineSource.DEMONSTRATED_INTEREST ? (
                                    <div className="flex items-center gap-2">
                                      {deadline.info && (
                                        <button
                                          className="view-details-btn bg-blue-600 dark:bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-700"
                                          onClick={() => {
                                            if (deadline.info) {
                                              // Extract URL from info field (could be plain URL or in text)
                                              const urlMatch = deadline.info.match(/(https?:\/\/[^\s]+)/);
                                              const url = urlMatch ? urlMatch[0] : deadline.info;
                                              window.open(url, '_blank', 'noopener,noreferrer');
                                            }
                                          }}
                                        >
                                          <IoOpenOutline className="inline mr-1" />
                                          Register
                                        </button>
                                      )}
                                      <button
                                        className={`view-details-btn ${
                                          deadline.completed
                                            ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                                            : 'bg-green-600 dark:bg-green-600 hover:bg-green-700 dark:hover:bg-green-700'
                                        }`}
                                        onClick={() => {
                                          if (!deadline.completed) {
                                            completeDeadline(
                                              { id: deadline.id, completed: true },
                                              {
                                                onSuccess: () => {
                                                  ToastService.showSuccess('Registered and marked complete!');
                                                },
                                                onError: () => {
                                                  ToastService.showError('Failed to mark as complete');
                                                },
                                              }
                                            );
                                          }
                                        }}
                                        disabled={deadline.completed}
                                        title="Confirm you've registered"
                                      >
                                        <IoCheckmark className="inline mr-1" />
                                        {deadline.completed ? 'Registered' : 'Confirm'}
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      className={`view-details-btn ${
                                        deadline.completed
                                          ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                                          : 'bg-green-600 dark:bg-green-600 hover:bg-green-700 dark:hover:bg-green-700'
                                      }`}
                                      onClick={() => {
                                        if (!deadline.completed) {
                                          completeDeadline(
                                            { id: deadline.id, completed: true },
                                            {
                                              onSuccess: () => {
                                                ToastService.showSuccess('Deadline marked as complete!');
                                              },
                                              onError: () => {
                                                ToastService.showError('Failed to mark deadline as complete');
                                              },
                                            }
                                          );
                                        }
                                      }}
                                      disabled={deadline.completed}
                                    >
                                      <IoCheckmark className="inline mr-1" />
                                      {deadline.completed ? 'Completed' : 'Mark Complete'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Calendar View */}
            {viewMode === 'calendar' && (
              <div className="calendar-view-content">
                <div className="calendar-header dark:bg-gray-800 dark:border-gray-700">
                  <div className="calendar-nav">
                    <button
                      className="nav-btn dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      onClick={goToPreviousMonth}
                    >
                      <IoChevronBack />
                    </button>
                    <h2 className="calendar-month dark:text-gray-100">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h2>
                    <button
                      className="nav-btn dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      onClick={goToNextMonth}
                    >
                      <IoChevronForward />
                    </button>
                  </div>
                  <button
                    className="today-btn dark:bg-blue-600 dark:hover:bg-blue-700"
                    onClick={goToToday}
                  >
                    <IoCalendarOutline className="mr-2" />
                    Today
                  </button>
                </div>

                <div className="calendar-grid dark:bg-gray-800 dark:border-gray-700">
                  <div className="calendar-weekdays">
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(
                      (day) => (
                        <div key={day} className="weekday-header dark:text-gray-300 dark:border-gray-700">
                          {day}
                        </div>
                      )
                    )}
                  </div>

                  <div className="calendar-days">
                    {calendarDays.map((dayInfo, index) => {
                      const dayDeadlines = dayInfo.isCurrentMonth
                        ? getDeadlinesForDate(dayInfo.date)
                        : [];

                      return (
                        <div
                          key={index}
                          className={`calendar-day ${
                            !dayInfo.isCurrentMonth
                              ? 'other-month dark:bg-gray-900 dark:border-gray-700'
                              : dayInfo.isToday
                              ? 'today dark:bg-blue-900/30 dark:border-blue-600'
                              : 'dark:bg-gray-800 dark:border-gray-700'
                          }`}
                        >
                          <div className="day-number dark:text-gray-100">{dayInfo.day}</div>
                          {dayInfo.isToday && <div className="today-label dark:bg-blue-600">Today</div>}
                          <div className="calendar-events">
                            {dayDeadlines.map((deadline) => {
                              const calendarColors = getCalendarEventColor(deadline.source_type);
                              return (
                                <div
                                  key={deadline.id}
                                  className={`calendar-event ${calendarColors} cursor-pointer hover:opacity-80`}
                                  onClick={() => handleEditClick(deadline.id)}
                                >
                                  <div className="event-title text-sm font-medium">
                                    {deadline.title}
                                  </div>
                                  <div className="event-subtitle text-xs">
                                    {formatPriority(deadline.priority)} Priority
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="summary-cards">
                  <div className="summary-card dark:bg-gray-800 dark:border-gray-700">
                    <div className="summary-header">
                      <div className="summary-icon bg-blue-100 dark:bg-blue-900/30">
                        <IoCheckmarkCircle className="text-blue-600 dark:text-blue-400 text-xl" />
                      </div>
                      <span className="summary-number dark:text-gray-100">
                        {deadlines.filter((d) => d.source_type === 'university_deadline').length}
                      </span>
                    </div>
                    <h3 className="summary-title dark:text-gray-100">University Deadlines</h3>
                    <p className="summary-subtitle dark:text-gray-400">From your college list</p>
                  </div>

                  <div className="summary-card dark:bg-gray-800 dark:border-gray-700">
                    <div className="summary-header">
                      <div className="summary-icon bg-green-100 dark:bg-green-900/30">
                        <IoCash className="text-green-600 dark:text-green-400 text-xl" />
                      </div>
                      <span className="summary-number dark:text-gray-100">
                        {deadlines.filter((d) => d.priority === 'high').length}
                      </span>
                    </div>
                    <h3 className="summary-title dark:text-gray-100">High Priority</h3>
                    <p className="summary-subtitle dark:text-gray-400">Urgent deadlines</p>
                  </div>

                  <div className="summary-card dark:bg-gray-800 dark:border-gray-700">
                    <div className="summary-header">
                      <div className="summary-icon bg-purple-100 dark:bg-purple-900/30">
                        <IoStar className="text-purple-600 dark:text-purple-400 text-xl" />
                      </div>
                      <span className="summary-number dark:text-gray-100">
                        {deadlines.filter((d) => !d.completed).length}
                      </span>
                    </div>
                    <h3 className="summary-title dark:text-gray-100">Incomplete</h3>
                    <p className="summary-subtitle dark:text-gray-400">Still to complete</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Deadline Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header dark:border-gray-700">
              <h2 className="modal-title dark:text-gray-100">Add Custom Deadline</h2>
              <button
                className="modal-close dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setShowAddModal(false)}
              >
                <IoClose />
              </button>
            </div>
            <form onSubmit={handleAddDeadline} className="modal-form">
              <div className="form-field">
                <label className="form-label dark:text-gray-200">
                  Task Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="taskName"
                  required
                  className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  placeholder="Enter task name"
                />
              </div>
              <div className="form-field">
                <label className="form-label dark:text-gray-200">
                  Deadline (Date & Time) <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="deadlineDatetime"
                  required
                  className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div className="form-field">
                <label className="form-label dark:text-gray-200">Priority</label>
                <select
                  name="priority"
                  className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  defaultValue={DeadlinePriority.MEDIUM}
                >
                  <option value={DeadlinePriority.HIGH}>High</option>
                  <option value={DeadlinePriority.MEDIUM}>Medium</option>
                  <option value={DeadlinePriority.LOW}>Low</option>
                </select>
              </div>
              <div className="form-field">
                <label className="form-label dark:text-gray-200">Additional Info (Optional)</label>
                <textarea
                  name="info"
                  rows={3}
                  className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  placeholder="Add notes, links, or additional details"
                />
              </div>
              <div className="form-field">
                <label className="form-label dark:text-gray-200">Est. Time (Optional)</label>
                <div className="time-inputs">
                  <input
                    type="number"
                    name="estHours"
                    min="0"
                    className="form-input time-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    placeholder="Hours"
                  />
                  <input
                    type="number"
                    name="estMinutes"
                    min="0"
                    max="59"
                    className="form-input time-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    placeholder="Minutes"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  onClick={() => setShowAddModal(false)}
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn dark:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isCreating}
                >
                  {isCreating ? 'Adding...' : 'Add Deadline'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Deadline Modal */}
      {showEditModal && selectedDeadline && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header dark:border-gray-700">
              <h2 className="modal-title dark:text-gray-100">Options for {selectedDeadline.title}</h2>
              <button
                className="modal-close dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setShowEditModal(false)}
              >
                <IoClose />
              </button>
            </div>
            <div className="edit-options-container">{renderEditOptions()}</div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && selectedDeadline && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirmModal(false)}>
          <div className="modal-content dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header dark:border-gray-700">
              <h2 className="modal-title dark:text-gray-100">Delete Deadline</h2>
              <button
                className="modal-close dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setShowDeleteConfirmModal(false)}
              >
                <IoClose />
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to delete <span className="font-semibold">"{selectedDeadline.title}"</span>?
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  onClick={() => setShowDeleteConfirmModal(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-btn bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    deleteDeadline(selectedDeadline.id, {
                      onSuccess: () => {
                        ToastService.showSuccess('Deadline deleted successfully!');
                        setShowDeleteConfirmModal(false);
                        setSelectedDeadlineId(null);
                      },
                      onError: () => {
                        ToastService.showError('Failed to delete deadline');
                      },
                    });
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {showEditFormModal && selectedDeadline && (
        <div className="modal-overlay" onClick={() => setShowEditFormModal(false)}>
          <div className="modal-content dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header dark:border-gray-700">
              <h2 className="modal-title dark:text-gray-100">Edit Deadline</h2>
              <button
                className="modal-close dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setShowEditFormModal(false)}
              >
                <IoClose />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const title = formData.get('taskName') as string;
                const deadlineDatetime = formData.get('deadlineDatetime') as string;
                const priority = formData.get('priority') as DeadlinePriority;
                const info = formData.get('info') as string;
                const estHours = formData.get('estHours') as string;
                const estMinutes = formData.get('estMinutes') as string;

                const hours = estHours ? parseFloat(estHours) : 0;
                const minutes = estMinutes ? parseFloat(estMinutes) : 0;
                const expectedHours = Math.round((hours + minutes / 60) * 10) / 10;

                updateDeadline(
                  {
                    id: selectedDeadline.id,
                    data: {
                      title,
                      due_date: new Date(deadlineDatetime).toISOString(),
                      priority,
                      info: info || undefined,
                      expected_hours: expectedHours > 0 ? expectedHours : undefined,
                    },
                  },
                  {
                    onSuccess: () => {
                      ToastService.showSuccess('Deadline updated successfully!');
                      setShowEditFormModal(false);
                      setSelectedDeadlineId(null);
                    },
                    onError: (error: any) => {
                      const errorMessage = error?.response?.data?.detail || 'Failed to update deadline';
                      ToastService.showError(errorMessage);
                    },
                  }
                );
              }}
              className="modal-form"
            >
              <div className="form-field">
                <label className="form-label dark:text-gray-200">
                  Task Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="taskName"
                  required
                  defaultValue={selectedDeadline.title}
                  className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  placeholder="Enter task name"
                />
              </div>
              <div className="form-field">
                <label className="form-label dark:text-gray-200">
                  Deadline (Date & Time) <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="deadlineDatetime"
                  required
                  defaultValue={new Date(selectedDeadline.due_date).toISOString().slice(0, 16)}
                  className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div className="form-field">
                <label className="form-label dark:text-gray-200">Priority</label>
                <select
                  name="priority"
                  className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  defaultValue={selectedDeadline.priority}
                >
                  <option value={DeadlinePriority.HIGH}>High</option>
                  <option value={DeadlinePriority.MEDIUM}>Medium</option>
                  <option value={DeadlinePriority.LOW}>Low</option>
                </select>
              </div>
              <div className="form-field">
                <label className="form-label dark:text-gray-200">Additional Info (Optional)</label>
                <textarea
                  name="info"
                  rows={3}
                  defaultValue={selectedDeadline.info || ''}
                  className="form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  placeholder="Add notes, links, or additional details"
                />
              </div>
              <div className="form-field">
                <label className="form-label dark:text-gray-200">Est. Time (Optional)</label>
                <div className="time-inputs">
                  <input
                    type="number"
                    name="estHours"
                    min="0"
                    defaultValue={
                      selectedDeadline.expected_hours
                        ? Math.floor(selectedDeadline.expected_hours)
                        : ''
                    }
                    className="form-input time-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    placeholder="Hours"
                  />
                  <input
                    type="number"
                    name="estMinutes"
                    min="0"
                    max="59"
                    defaultValue={
                      selectedDeadline.expected_hours
                        ? Math.round((selectedDeadline.expected_hours % 1) * 60)
                        : ''
                    }
                    className="form-input time-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    placeholder="Minutes"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  onClick={() => setShowEditFormModal(false)}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn dark:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Updating...' : 'Update Deadline'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deadlines;
